import React, { useEffect, useMemo, useState } from 'react';

import {
    GetBasePlanLaunchDatesInput,
    GetBasePlanLaunchDatesOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColumnState, CsvExportParams, GridApi } from 'ag-grid-community';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { Status } from 'src/constants/enums';
import { MAPLE_ROLE_SET } from 'src/constants/role';
import { ROUTES, URL_PARAMS } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBasePlanLaunchDateRowData } from 'src/types/IBasePlanLaunchDate';
import { getCurrentDate } from 'src/util/dateTimeUtil';

import { AddBasePlanLaunchDate } from '../AddBasePlanLaunchDate';
import { BaseAgGrid } from '../BaseAgGrid';
import { FilterInput } from '../FilterInput';
import { ProtectedRoute } from '../ProtectedRoute';
import './BasePlanLaunchDates.scss';
import { CancelBasePlanLaunchDateModal } from './CancelBasePlanLaunchDateModal';
import { basePlanLaunchDatesColDefs } from './basePlanLaunchDatesColDefs';

export const BasePlanLaunchDates = () => {
    const [basePlanLaunchDatesRowData, setBasePlanLaunchDatesRowData] = useState<IBasePlanLaunchDateRowData[]>([]);
    const [selectedBasePlanLaunchDateToCancel, setSelectedBasePlanLaunchDateToCancel] =
        useState<IBasePlanLaunchDateRowData>();
    const [filterText, setFilterText] = useState('');
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const { userRole, setTopAlertMsg } = React.useContext(AppContext);
    const history = useHistory();
    const sortModel: ColumnState[] = [{ colId: 'lastUpdated', sort: 'desc', sortIndex: 0 }];

    const colDefs = useMemo(() => {
        const actionCol = {
            filter: false,
            cellRenderer: (params: any) => (
                <Column height="100%" alignmentVertical="center">
                    <Row spacing="200">
                        {!params.data.isDeleted && (
                            <Button
                                type="link"
                                size="small"
                                href={`${ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX}${ROUTES.BASE_PLAN_LAUNCH_DATES.EDIT}/${params.data.basePlanLaunchDateId}${ROUTES.BASE_PLAN_LAUNCH_DATES.STEP.DETAILS}`}
                                onClick={history.push}
                                disabled={!MAPLE_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                            </Button>
                        )}
                        {!params.data.isDeleted && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => setSelectedBasePlanLaunchDateToCancel(params.data)}
                                disabled={!MAPLE_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={closeCircleTokens} />
                            </Button>
                        )}
                    </Row>
                </Column>
            ),
            minWidth: 120,
            maxWidth: 120
        };

        return [...basePlanLaunchDatesColDefs, actionCol];
    }, []);

    useEffect(() => {
        if (pathname !== ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX) return;

        getBasePlanLaunchDates();
    }, [pathname]);

    const getBasePlanLaunchDates = () => {
        contractConfigurationClient
            .getBasePlanLaunchDates(
                GetBasePlanLaunchDatesInput.clone({
                    includeDeleted: true
                })
            )
            .subscribe(
                (response: GetBasePlanLaunchDatesOutput) => {
                    setIsDataLoaded(true);
                    setBasePlanLaunchDatesRowData(mapResponseToRowData(response) || []);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getBasePlanLaunchDates unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const mapResponseToRowData = (response: GetBasePlanLaunchDatesOutput) => {
        return response.basePlanLaunchDates?.map((basePlanLaunchDate) => {
            return {
                basePlanName: basePlanLaunchDate.basePlan?.planName || '',
                basePlanLaunchDateId: basePlanLaunchDate.basePlanLaunchDateId || '',
                territoryCode: basePlanLaunchDate.territory || '',
                startDate: basePlanLaunchDate.startDate?.toString() || '',
                endDate: basePlanLaunchDate.endDate?.toString() || '',
                lastUpdated:
                    basePlanLaunchDate.changedDate?.toString() || basePlanLaunchDate.creationDate?.toString() || '',
                updatedBy: basePlanLaunchDate.changedBy || basePlanLaunchDate.createdBy || '',
                created: basePlanLaunchDate.creationDate?.toString() || '',
                createdBy: basePlanLaunchDate.createdBy || '',
                isDeleted: basePlanLaunchDate.isDeleted
            } as IBasePlanLaunchDateRowData;
        });
    };

    const handleBasePlanLaunchDateModalOnClose = (refreshData = false) => {
        setSelectedBasePlanLaunchDateToCancel(undefined);
        if (refreshData) getBasePlanLaunchDates();
    };

    const exportBasePlanLaunchDates = () => {
        const params: CsvExportParams = {
            fileName: `base_plan_export_${getCurrentDate()}.csv`,
            columnKeys: basePlanLaunchDatesColDefs.flatMap((colDef) => (colDef.field ? [colDef.field] : []))
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [Status.Active, Status.Ended, Status.Upcoming, Status.Draft]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const basePlanLaunchDatesContent = (
        <Column className="base-plan-launch-dates-content" spacing="300" width="100%" height="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <div>
                        <Button type="link" size="small" onClick={exportBasePlanLaunchDates}>
                            <Icon tokens={downloadLargeTokens} />
                            Download
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            href={`${ROUTES.BASE_PLAN_LAUNCH_DATES.INDEX}${ROUTES.BASE_PLAN_LAUNCH_DATES.ADD}${ROUTES.BASE_PLAN_LAUNCH_DATES.STEP.DETAILS}`}
                            onClick={history.push}
                            disabled={!MAPLE_ROLE_SET.has(userRole)}
                        >
                            <Icon tokens={plusCircleTokens} />
                            Add launch date
                        </Button>
                    </div>
                </Row>
            </Box>
            <div className="base-plan-launch-dates-grid">
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={basePlanLaunchDatesRowData}
                    filterText={filterText}
                    sortModel={sortModel}
                    setAgGridApi={setAgGridApi}
                    onFirstDataRendered={setDefaultFilter}
                    loading={!isDataLoaded}
                />
            </div>
            <CancelBasePlanLaunchDateModal
                basePlanLaunchDate={selectedBasePlanLaunchDateToCancel}
                isOpen={!!selectedBasePlanLaunchDateToCancel}
                onClose={handleBasePlanLaunchDateModalOnClose}
            />
        </Column>
    );

    return (
        <div className="base-plan-launch-dates-container">
            <Switch>
                <ProtectedRoute exact path={path}>
                    {basePlanLaunchDatesContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={MAPLE_ROLE_SET} path={`${path}${ROUTES.BASE_PLAN_LAUNCH_DATES.ADD}`}>
                    <AddBasePlanLaunchDate />
                </ProtectedRoute>
                <ProtectedRoute
                    requiredRoleSet={MAPLE_ROLE_SET}
                    path={`${path}${ROUTES.BASE_PLAN_LAUNCH_DATES.EDIT}${URL_PARAMS.BASE_PLAN_LAUNCH_DATE_ID}`}
                >
                    <AddBasePlanLaunchDate />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
